import React, { useEffect, useState } from 'react';

import { BusinessSignup } from './BusinessSignup/BusinessSignup';
import { SignupTypes } from './constants';
import { CreateAccount } from './CreateAccount/CreateAccount';
import { Container } from './Credentials.styles';
import {
    BusinessUser,
    GoogleTracker,
    HubspotTracker,
    MixPanelTracker,
    SessionInfo,
    SignupFormState
} from './Credentials.types';
import { ForgotPassword } from './ForgotPassword/ForgotPassword';
import { ResetPassword } from './ResetPassword/ResetPassword';
import { Backdrop } from './shared.styles';
import { SignIn } from './SignIn/SignIn';
import { SignUp } from './Signup/SignUp';

export interface CredentialsProps {
    canSignup: boolean;
    email: string | null;
    lang: string;
    proposedPage?: CredentialsPages;
    googleTracker: GoogleTracker;
    hubspotTracker: HubspotTracker;
    mixPanelTracker: MixPanelTracker;
    wantsSignup: boolean;
    afterLogin: (loginResult?: { loginSuccessful?: boolean; justSignedUp?: boolean }) => void;
    changeExpiredPassword: (params: { userId: string; oldPassword: string; newPassword: string }) => Promise<void>;
    forgotPassword: (email: string) => Promise<void>;
    googleLogin: (isBusinessSignup: boolean) => Promise<{ justSignedUp: boolean } & SessionInfo>;
    login: (params: { email: string; password: string }) => Promise<SessionInfo>;
    logout: () => Promise<void>;
    onPageChange?: (page: string) => boolean;
    signup: (params: {
        email: string;
        password: string;
        marketing: boolean;
        isBusiness: boolean;
    }) => Promise<SessionInfo>;
    updateUserDetails: (params: BusinessUser) => Promise<void>;
}

export interface SignUpProps extends CredentialsProps {
    formState: SignupFormState;
    signupType: string;
    userId: string;
    setUserId: (userId: string) => void;
    setFormState: (state: Partial<SignupFormState>) => void;
    setSignupType: (type: SignupTypes) => void;
    switchPage: (page: CredentialsPages) => void;
}

const route = {
    SignUp: SignUp,
    CreateAccount: CreateAccount,
    BusinessSignup: BusinessSignup,
    SignIn: SignIn,
    ResetPassword: ResetPassword,
    ForgotPassword: ForgotPassword
} as const;

export type CredentialsPages = keyof typeof route;

export const Credentials = ({
    wantsSignup,
    email,
    proposedPage,
    onPageChange,
    mixPanelTracker,
    hubspotTracker,
    googleTracker,
    ...props
}: CredentialsProps) => {
    const [signupType, setSignupType] = useState(SignupTypes.A);
    const [page, setPage] = useState<CredentialsPages>(wantsSignup ? 'SignUp' : 'SignIn');
    const [userId, setUserId] = useState('');
    const [signupFormState, setSignupFormState] = useState<SignupFormState>({
        password: '',
        email: email ?? '',
        displayName: '',
        locale: '',
        isBusiness: true,
        marketing: false
    });

    useEffect(() => {
        if (!proposedPage) return;

        setPage(proposedPage);
    }, [proposedPage]);

    const handlePageChange = (newPage: CredentialsPages) => {
        if (onPageChange) {
            const allowChange = onPageChange(newPage);

            if (allowChange) {
                setPage(newPage);
            }
        } else {
            setPage(newPage);
        }
    };

    const handleSetSignupFormState = (partialState: Partial<SignupFormState>) => {
        setSignupFormState({ ...signupFormState, ...partialState });
    };

    const CurrentPage = route[page] || SignIn;

    return (
        <Backdrop>
            <Container>
                <CurrentPage
                    {...props}
                    email={signupFormState.email}
                    formState={signupFormState}
                    googleTracker={googleTracker}
                    hubspotTracker={hubspotTracker}
                    mixPanelTracker={mixPanelTracker}
                    setFormState={handleSetSignupFormState}
                    setSignupType={setSignupType}
                    setUserId={setUserId}
                    signupType={signupType}
                    switchPage={handlePageChange}
                    userId={userId}
                    wantsSignup={wantsSignup}
                />
            </Container>
        </Backdrop>
    );
};
